import { Listing } from '../types';

// Image hosting: the legacy CDN belongs to the data seller and photo
// licensing is unconfirmed. Images render only when an image base is
// explicitly configured (own mirror or licensed CDN); otherwise hidden.
export const IMAGE_BASE = process.env.REACT_APP_IMAGE_BASE || '';

export const listingImageUrl = (listing: Listing, size: number, fileName?: string): string | null => {
  if (!fileName) return null;
  if (fileName.startsWith('http://') || fileName.startsWith('https://')) return fileName;
  if (!IMAGE_BASE) return null;
  return `${IMAGE_BASE}/${listing.publicId}/${size}/${fileName}`;
};

export const getListingPhoto = (listing: Listing): string | null => {
  if (listing.photos && listing.photos.length > 0) {
    return listingImageUrl(listing, 800, listing.photos[0].fileName);
  }
  return null;
};

/**
 * Rating display transition rule (single source of truth):
 * once a listing has >=5 first-party verified reviews, they become the
 * headline rating and the licensed historical aggregate moves to a
 * footnote; below that, the historical aggregate headlines but is
 * always labeled as historical/licensed.
 */
export const FIRST_PARTY_HEADLINE_THRESHOLD = 5;

export interface RatingDisplay {
  headlineValue: number;
  headlineCount: number;
  headlineSource: 'first-party' | 'historical' | 'none';
  historicalValue: number;
  historicalCount: number;
  firstPartyValue: number;
  firstPartyCount: number;
}

export const getRatingDisplay = (listing: Listing): RatingDisplay => {
  const historicalValue = listing.ratings?.overall?.value || 0;
  const historicalCount = listing.ratings?.publishedReviewsCount || 0;
  const firstPartyValue = listing.firstPartyRatings?.value || 0;
  const firstPartyCount = listing.firstPartyRatings?.count || 0;

  if (firstPartyCount >= FIRST_PARTY_HEADLINE_THRESHOLD) {
    return {
      headlineValue: firstPartyValue, headlineCount: firstPartyCount, headlineSource: 'first-party',
      historicalValue, historicalCount, firstPartyValue, firstPartyCount,
    };
  }
  if (historicalCount > 0) {
    return {
      headlineValue: historicalValue, headlineCount: historicalCount, headlineSource: 'historical',
      historicalValue, historicalCount, firstPartyValue, firstPartyCount,
    };
  }
  return {
    headlineValue: firstPartyValue, headlineCount: firstPartyCount,
    headlineSource: firstPartyCount > 0 ? 'first-party' : 'none',
    historicalValue, historicalCount, firstPartyValue, firstPartyCount,
  };
};

export const getStarPercentage = (listing: Listing): number => {
  const rating = listing.ratings?.overall?.value || 0;
  return (rating / 5) * 100;
};

export const getRatingLabel = (rating: number): string => {
  if (rating >= 4.5) return 'Excellent';
  if (rating >= 4.0) return 'Great';
  if (rating >= 3.5) return 'Very Good';
  if (rating >= 3.0) return 'Good';
  return '';
};

export const formatPhone = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

export const stateNameToSlug = (name: string): string =>
  name.toLowerCase().replace(/\s+/g, '-');

export const stateAbbreviations: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas',
  CA: 'California', CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho',
  IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas',
  KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi',
  MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma',
  OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah',
  VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia',
  WI: 'Wisconsin', WY: 'Wyoming', DC: 'District of Columbia',
};
